// Pagina pubblica — apertura richiesta intervento senza login
// Protetta da Cloudflare Turnstile CAPTCHA
import { useState, useEffect, useRef } from 'react'
import { CheckCircle, Phone, Mail, Building2, User, AlertCircle } from 'lucide-react'
import api from '../../lib/api'

// Site key Cloudflare Turnstile
// Usa VITE_TURNSTILE_SITE_KEY in produzione (registrati su dash.cloudflare.com → Turnstile)
// La chiave di test "1x00000000000000000000AA" passa sempre senza mostrare challenge
const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA'

// Dichiarazione globale window.turnstile (caricato via CDN)
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
    }
  }
}

type Priority = 'low' | 'medium' | 'high' | 'critical'

const PRIORITIES: { value: Priority; label: string; color: string }[] = [
  { value: 'low',      label: 'Bassa',    color: 'border-gray-300 text-gray-700' },
  { value: 'medium',   label: 'Media',    color: 'border-blue-400 text-blue-700' },
  { value: 'high',     label: 'Alta',     color: 'border-orange-400 text-orange-700' },
  { value: 'critical', label: 'Critica',  color: 'border-red-400 text-red-700' },
]

interface FormData {
  companyName: string
  contactName: string
  contactEmail: string
  contactPhone: string
  title: string
  description: string
  priority: Priority
}

const EMPTY: FormData = {
  companyName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  title: '',
  description: '',
  priority: 'medium',
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition'

export function PublicRequestPage() {
  const [form, setForm] = useState<FormData>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [ticketNumber, setTicketNumber] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [captchaReady, setCaptchaReady] = useState(false)
  const turnstileContainerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string>('')

  // Carica script Turnstile via CDN e renderizza il widget
  useEffect(() => {
    if (document.getElementById('cf-turnstile-script')) {
      // Script già presente (es. hot-reload dev)
      initTurnstile()
      return
    }
    const script = document.createElement('script')
    script.id = 'cf-turnstile-script'
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = initTurnstile
    document.head.appendChild(script)
  }, [])

  function initTurnstile() {
    if (!turnstileContainerRef.current || !window.turnstile) return
    widgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setCaptchaToken(token)
        setCaptchaReady(true)
      },
      'expired-callback': () => {
        setCaptchaToken('')
        setCaptchaReady(false)
      },
      'error-callback': () => {
        setCaptchaToken('')
        setCaptchaReady(false)
      },
    })
  }

  const set = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setForm((f) => ({ ...f, [field]: e.target.value }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.companyName || !form.contactName || !form.contactEmail || !form.title) {
      setError('Compila tutti i campi obbligatori.')
      return
    }
    if (!captchaToken) {
      setError('Completa la verifica CAPTCHA.')
      return
    }
    setSubmitting(true)
    try {
      const res = await api.post<{ ticketNumber: string }>('/portal/public-request', {
        companyName: form.companyName,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone || undefined,
        title: form.title,
        description: form.description || undefined,
        priority: form.priority,
        cfTurnstileToken: captchaToken,
      })
      setTicketNumber(res.data.ticketNumber)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore durante l\'invio. Riprova.'
      setError(msg)
      // Reset widget dopo errore
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current)
        setCaptchaToken('')
        setCaptchaReady(false)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (ticketNumber) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Richiesta inviata!</h2>
          <p className="text-gray-600 text-sm">
            La tua richiesta è stata ricevuta. Il nostro team la prenderà in carico al più presto.
          </p>
          <div className="rounded-xl bg-gray-50 border border-gray-200 px-5 py-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Numero richiesta</p>
            <p className="text-2xl font-bold text-blue-600">{ticketNumber}</p>
          </div>
          <p className="text-xs text-gray-400">
            Salva questo numero per eventuali comunicazioni. Riceverai aggiornamenti via email.
          </p>
          <button
            onClick={() => { setForm(EMPTY); setTicketNumber(null); setCaptchaToken(''); setCaptchaReady(false) }}
            className="text-sm text-blue-600 hover:underline"
          >
            Invia un'altra richiesta
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-blue-600 mb-4">
            <span className="text-white font-bold text-xl">FC</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Richiesta intervento</h1>
          <p className="text-gray-500 text-sm mt-1">
            Compila il modulo per aprire una richiesta di assistenza tecnica
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 space-y-5">
          {/* Dati azienda */}
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" />
              Dati azienda
            </p>
          </div>

          <Field label="Nome azienda" required>
            <input
              className={inputCls}
              placeholder="Acme S.r.l."
              value={form.companyName}
              onChange={set('companyName')}
            />
          </Field>

          {/* Dati referente */}
          <div className="space-y-1 pt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              Referente
            </p>
          </div>

          <Field label="Nome e cognome" required>
            <input
              className={inputCls}
              placeholder="Mario Rossi"
              value={form.contactName}
              onChange={set('contactName')}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" required>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  className={inputCls + ' pl-9'}
                  placeholder="mario@acme.it"
                  value={form.contactEmail}
                  onChange={set('contactEmail')}
                />
              </div>
            </Field>
            <Field label="Telefono">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  className={inputCls + ' pl-9'}
                  placeholder="+39 02 1234567"
                  value={form.contactPhone}
                  onChange={set('contactPhone')}
                />
              </div>
            </Field>
          </div>

          {/* Dettagli richiesta */}
          <div className="space-y-1 pt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Dettagli richiesta
            </p>
          </div>

          <Field label="Oggetto richiesta" required>
            <input
              className={inputCls}
              placeholder="Es. Malfunzionamento impianto HVAC piano 2"
              value={form.title}
              onChange={set('title')}
            />
          </Field>

          <Field label="Descrizione">
            <textarea
              className={inputCls + ' resize-none'}
              rows={4}
              placeholder="Descrivi il problema nel dettaglio: quando si è manifestato, quali sintomi, urgenza..."
              value={form.description}
              onChange={set('description')}
            />
          </Field>

          <Field label="Priorità">
            <div className="grid grid-cols-4 gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
                  className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                    form.priority === p.value
                      ? p.color + ' bg-opacity-10 bg-current'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Field>

          {/* Cloudflare Turnstile CAPTCHA */}
          <div className="flex justify-center">
            <div ref={turnstileContainerRef} />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !captchaReady}
            className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
          >
            {submitting ? 'Invio in corso…' : 'Invia richiesta'}
          </button>

          <p className="text-center text-xs text-gray-400">
            Hai un account?{' '}
            <a href="/portal/login" className="text-blue-600 hover:underline">
              Accedi al portale clienti
            </a>
          </p>
        </form>
      </div>
    </div>
  )
}
